import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import type { InsurancePolicy } from 'src/interfaces/insurancePolicy'
import type { MenuActionItem } from 'src/interfaces/ui'
import { getInsurancePolicyDeletingPermanentId } from 'src/selectors/insurancePolicy'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  policy: InsurancePolicy
  onEdit(policy: InsurancePolicy): void
  onDelete(policy: InsurancePolicy): void
}

// The row itself opens the policy, so `RowActionsMenu` stops the click at the cell.
const InsurancePolicyActionsCellComponent = ({ policy, onEdit, onDelete }: Props) => {
  const deletingPermanentId = useSelector(getInsurancePolicyDeletingPermanentId)
  const isDeleting = deletingPermanentId === policy.permanentId

  const items = useMemo<MenuActionItem[]>(() => {
    return [
      {
        label: 'Rename',
        icon: <GSIcon iconName="gs_pencil" size={20} css={tw`text-shades-1000`} />,
        disabled: isDeleting,
        dataTestId: `insurance-policies-list-edit-${policy.permanentId}`,
        onClick: () => {
          onEdit(policy)
        },
      },
      {
        label: isDeleting ? 'Deleting…' : 'Delete',
        icon: <GSIcon iconName="gs_trash_can" size={20} css={tw`text-critical-500`} />,
        color: 'var(--mui-critical-500)',
        disabled: isDeleting,
        dataTestId: `insurance-policies-list-delete-${policy.permanentId}`,
        onClick: () => {
          onDelete(policy)
        },
      },
    ]
  }, [policy, isDeleting, onEdit, onDelete])

  return (
    <RowActionsMenu
      items={items}
      ariaLabel="Insurance policy actions"
      dataTestId={`insurance-policies-list-actions-${policy.permanentId}`}
    />
  )
}

export const InsurancePolicyActionsCell = memo(InsurancePolicyActionsCellComponent)
